import React, { useEffect, useState } from 'react';
import PhoneInput from '../UI/PhoneInput';
import { apiUrl } from '../../config/api';
import { DocumentIcon } from '../UI/IconLibrary';
import { FIELD_LABEL, FIELD_READONLY } from '../UI/DisplayField';
import DocumentTile, { DOCUMENT_GRID } from '../UI/DocumentTile';

// Map a document_type to the replace-endpoint slug. Only these can be replaced
// in-place by staff for an unapproved client.
const DOC_TYPE_SLUG = {
  'Identification': 'identification',
  'Payslip': 'payslip',
  'Bank Statement': 'bank-statement',
  'Online Client Photo': 'photo',
  'Proof Of Residence': 'proof-of-residence',
};

const MARITAL_STATUSES = [
  'Single', 'Married', 'Divorced', 'Widowed', 'Separated', 'Customary Union', 'Other'
];
const GENDERS = ['Male', 'Female', 'Other'];

// Simple static country list for typeahead dropdown
const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi',
  'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo (Congo-Brazzaville)','Costa Rica','Côte d’Ivoire','Croatia','Cuba','Cyprus','Czechia',
  'Democratic Republic of the Congo','Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia',
  'Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary',
  'Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan',
  'Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway',
  'Oman',
  'Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal',
  'Qatar',
  'Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria',
  'Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Vanuatu','Vatican City','Venezuela','Vietnam',
  'Yemen',
  'Zambia','Zimbabwe'
];


const LABEL_CLS = FIELD_LABEL;
const FIELD_CLS = 'w-full';
// The shared field look (index.css, DisplayField): read-only fields as shown values.
const READONLY_CLS = FIELD_READONLY;
const REQ_BASE = 'w-full';
const reqBorder = (filled) => filled
  ? ''
  : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20';
const isFilled = (v) => String(v ?? '').trim() !== '';

const PersonalInfoForm = ({
  formData,
  setFormData,
  formErrors,
  idFile,
  setIdFile,
  idFileUrl,
  // Optional: pass document DB id and name to support API download
  idDocId,
  idDocName,
  // Proof Of Residence support
  porFile,
  setPorFile,
  porDocId,
  porDocName,
  documents = [],
  // Set when the host already shows these documents somewhere else (the
  // client workspace lifts them into a row of their own above both panels).
  hideDocuments = false,
  hideHeader = false,
  readOnly = false,
  isNewClient = false,
  showDocumentUploads = true,
  topContent = null,
  onFieldFocus,
  // Staff document replacement for not-yet-approved clients.
  allowDocReplace = false,
  replaceClientId = null,
  onDocReplaced = null,
}) => {
  const [replacingType, setReplacingType] = useState(null);

  const handleReplaceDoc = async (docType, file) => {
    const slug = DOC_TYPE_SLUG[docType];
    if (!slug || !file || !replaceClientId) return;
    setReplacingType(docType);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(apiUrl(`/api/clients/${replaceClientId}/documents/${slug}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.error || 'Failed to replace document.');
      if (typeof onDocReplaced === 'function') onDocReplaced();
    } catch (e) {
      alert(e.message || 'Failed to replace document.');
    } finally {
      setReplacingType(null);
    }
  };
  const hasExistingAttachments = Boolean(idDocId || idFileUrl || porDocId);

  // Default country to Namibia if empty and editable (runs on mount or when readOnly toggles)
  useEffect(() => {
    if (!readOnly && (!formData?.country || formData.country.trim() === '')) {
      setFormData(prev => ({ ...prev, country: 'Namibia' }));
    }
  }, [readOnly]);

  // No internal phone parsing needed; PhoneInput handles composition

  const readonlyDocs = (() => {
    // If caller provided a documents list (details view), show all of them.
    if (Array.isArray(documents) && documents.length > 0) return documents;
    const docs = [];
    if (idDocId) docs.push({ id: idDocId, document_type: 'Identification', file_path: idDocName || 'ID/Passport' });
    if (porDocId) docs.push({ id: porDocId, document_type: 'Proof Of Residence', file_path: porDocName || 'Proof Of Residence' });
    return docs;
  })();

  const handleFieldFocus = (field) => {
    if (!readOnly && typeof onFieldFocus === 'function') {
      onFieldFocus(field);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm p-6 text-sm form-compact">
      {!hideHeader && (
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Personal Information</h3>
        </div>
      )}
      {topContent && (
        <div className="mb-6">
          {topContent}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={LABEL_CLS}>Email</label>
          <input
            type="email"
            value={formData.email || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('email')}
            onChange={e => !readOnly && setFormData({ ...formData, email: e.target.value })}
            placeholder="Email"
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.email))}`}
            required
          />
          {!readOnly && formErrors.email && <p className="text-rose-500 text-xs mt-1">{formErrors.email}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>First Name</label>
          <input
            type="text"
            value={formData.first_name || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('first_name')}
            onChange={e => !readOnly && setFormData({ ...formData, first_name: e.target.value })}
            placeholder="First Name"
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.first_name))}`}
            required
          />
          {!readOnly && formErrors.first_name && <p className="text-rose-500 text-xs mt-1">{formErrors.first_name}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>Middle Name</label>
          <input
            type="text"
            value={formData.middle_name || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('middle_name')}
            onChange={e => !readOnly && setFormData({ ...formData, middle_name: e.target.value })}
            placeholder="Middle Name"
            className={`${FIELD_CLS} ${readOnly ? READONLY_CLS : ''}`}
          />
        </div>
        <div>
          <label className={LABEL_CLS}>Last Name</label>
          <input
            type="text"
            value={formData.last_name || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('last_name')}
            onChange={e => !readOnly && setFormData({ ...formData, last_name: e.target.value })}
            placeholder="Last Name"
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.last_name))}`}
            required
          />
          {!readOnly && formErrors.last_name && <p className="text-rose-500 text-xs mt-1">{formErrors.last_name}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>Gender</label>
          <select
            value={formData.gender || ''}
            disabled={readOnly}
            onFocus={() => handleFieldFocus('gender')}
            onChange={e => !readOnly && setFormData({ ...formData, gender: e.target.value })}
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.gender))}`}
            required
          >
            <option value="">Select Gender</option>
            {GENDERS.map(gender => (
              <option key={gender} value={gender}>{gender}</option>
            ))}
          </select>
          {!readOnly && formErrors.gender && <p className="text-rose-500 text-xs mt-1">{formErrors.gender}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>ID/Passport No</label>
          <input
            type="text"
            value={formData.id_passport_no || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('id_passport_no')}
            onChange={e => !readOnly && setFormData({ ...formData, id_passport_no: e.target.value })}
            placeholder="ID/Passport No"
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.id_passport_no))}`}
            required
          />
          {!readOnly && formErrors.id_passport_no && <p className="text-rose-500 text-xs mt-1">{formErrors.id_passport_no}</p>}
        </div>
        <div>
          <PhoneInput
            label="Phone Number"
            value={formData.phone_number || ''}
            onChange={(combined) => setFormData({ ...formData, phone_number: combined })}
            onInputFocus={() => handleFieldFocus('phone_number')}
            required={!readOnly}
            readOnly={readOnly}
            inputClassName={`px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 rounded-lg text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 transition-colors ${readOnly ? `${READONLY_CLS} !border-gray-200 dark:!border-slate-600` : reqBorder(isFilled(formData.phone_number))}`}
            selectClassName={`px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 rounded-lg text-gray-900 dark:text-slate-100 transition-colors ${readOnly ? `${READONLY_CLS} !border-gray-200 dark:!border-slate-600` : reqBorder(isFilled(formData.phone_number))}`}
          />
          {!readOnly && formErrors.phone_number && <p className="text-rose-500 text-xs mt-1">{formErrors.phone_number}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>Postal Address</label>
          <input
            type="text"
            value={formData.postal_address || ''}
            readOnly={readOnly}
            onFocus={() => handleFieldFocus('postal_address')}
            onChange={e => !readOnly && setFormData({ ...formData, postal_address: e.target.value })}
            placeholder="Postal Address"
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.postal_address))}`}
            required
          />
          {!readOnly && formErrors.postal_address && <p className="text-rose-500 text-xs mt-1">{formErrors.postal_address}</p>}
        </div>
        {/* Structured Residential Address */}
        <div className="md:col-span-2">
          <label className={LABEL_CLS}>Residential Address</label>
          <div className="rounded-lg border border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-900/30 p-3 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <span className={LABEL_CLS}>ERF No</span>
              <input
                type="text"
                value={formData.erf_no || ''}
                readOnly={readOnly}
                onFocus={() => handleFieldFocus('erf_no')}
                onChange={e => !readOnly && setFormData({ ...formData, erf_no: e.target.value })}
                placeholder="ERF No"
                className={`${FIELD_CLS} ${readOnly ? READONLY_CLS : ''}`}
              />
            </div>
            <div>
              <span className={LABEL_CLS}>Street Name</span>
              <input
                type="text"
                value={formData.street_name || ''}
                readOnly={readOnly}
                onFocus={() => handleFieldFocus('street_name')}
                onChange={e => !readOnly && setFormData({ ...formData, street_name: e.target.value })}
                placeholder="Street Name"
                className={`${FIELD_CLS} ${readOnly ? READONLY_CLS : ''}`}
              />
            </div>
            <div>
              <span className={LABEL_CLS}>Location/Suburb</span>
              <input
                type="text"
                value={formData.suburb || ''}
                readOnly={readOnly}
                onFocus={() => handleFieldFocus('suburb')}
                onChange={e => !readOnly && setFormData({ ...formData, suburb: e.target.value })}
                placeholder="Location/Suburb"
                className={`${FIELD_CLS} ${readOnly ? READONLY_CLS : ''}`}
              />
            </div>
            <div>
              <span className={LABEL_CLS}>City/Town</span>
              <input
                type="text"
                value={formData.city || ''}
                readOnly={readOnly}
                onFocus={() => handleFieldFocus('city')}
                onChange={e => !readOnly && setFormData({ ...formData, city: e.target.value })}
                placeholder="City/Town"
                className={`${FIELD_CLS} ${readOnly ? READONLY_CLS : ''}`}
              />
            </div>
            <div>
              <span className={LABEL_CLS}>Country</span>
              {!readOnly ? (
                <>
                  <input
                    type="text"
                    list="countries-list"
                    value={formData.country || ''}
                    onFocus={() => handleFieldFocus('country')}
                    onChange={e => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Country (default Namibia)"
                    className={FIELD_CLS}
                    autoComplete="off"
                  />
                  <datalist id="countries-list">
                    {COUNTRIES.map(c => (
                      <option value={c} key={c} />
                    ))}
                  </datalist>
                </>
              ) : (
                <input
                  type="text"
                  value={formData.country || ''}
                  readOnly
                  className={`${FIELD_CLS} ${READONLY_CLS}`}
                />
              )}
            </div>
          </div>
          {/* Compose legacy residential_address to keep backward compatibility */}
          <input type="hidden" value={formData.residential_address || ''} readOnly />
          {!readOnly && formErrors.residential_address && (
            <p className="text-rose-500 text-xs mt-1">{formErrors.residential_address}</p>
          )}
        </div>
        <div>
          <label className={LABEL_CLS}>Marital Status</label>
          <select
            value={formData.marital_status || ''}
            disabled={readOnly}
            onFocus={() => handleFieldFocus('marital_status')}
            onChange={e => !readOnly && setFormData({ ...formData, marital_status: e.target.value })}
            className={readOnly ? `${FIELD_CLS} ${READONLY_CLS}` : `${REQ_BASE} ${reqBorder(isFilled(formData.marital_status))}`}
            required
          >
            <option value="">Select Marital Status</option>
            {MARITAL_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          {!readOnly && formErrors.marital_status && <p className="text-rose-500 text-xs mt-1">{formErrors.marital_status}</p>}
        </div>
        {!readOnly && showDocumentUploads ? (
          <div className="md:col-span-2">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-slate-700">
              <div className="flex items-center mb-3 pb-2 border-b dark:border-slate-700">
                <DocumentIcon className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-sm font-medium text-gray-800 dark:text-slate-100">Documents</h3>
              </div>

              <div className={DOCUMENT_GRID}>
                <DocumentTile
                  type="Identification"
                  doc={idDocId ? { id: idDocId, file_name: idDocName } : null}
                  file={idFile}
                  onPick={setIdFile}
                  onRemove={() => setIdFile(null)}
                  required={isNewClient}
                />
                <DocumentTile
                  type="Proof Of Residence"
                  doc={porDocId ? { id: porDocId, file_name: porDocName } : null}
                  file={porFile}
                  onPick={setPorFile}
                  onRemove={setPorFile ? () => setPorFile(null) : undefined}
                  required={isNewClient}
                />
              </div>
              {formErrors?.idFile && <p className="text-rose-500 text-xs mt-1">{formErrors.idFile}</p>}
              {formErrors?.porFile && <p className="text-rose-500 text-xs mt-1">{formErrors.porFile}</p>}

              {hasExistingAttachments && (
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-3">Leave empty to keep current files.</p>
              )}
            </div>
          </div>
        ) : (readOnly && !hideDocuments) ? (
          <div className="md:col-span-2">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-gray-100 dark:border-slate-700">
              <div className="flex items-center mb-4 pb-2 border-b dark:border-slate-700">
                <DocumentIcon className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-base font-medium text-gray-800 dark:text-slate-100">Documents</h3>
              </div>

              {readonlyDocs.length === 0 ? (
                <div className="text-gray-500 dark:text-slate-400 text-sm py-3">No documents found for this client.</div>
              ) : (
                <div className={DOCUMENT_GRID}>
                  {readonlyDocs.map((doc) => (
                    <DocumentTile
                      key={doc.id}
                      type={doc.document_type}
                      doc={doc}
                      accept={doc.document_type === 'Online Client Photo' ? 'image/*' : 'application/pdf'}
                      onPick={allowDocReplace && DOC_TYPE_SLUG[doc.document_type] ? (f) => handleReplaceDoc(doc.document_type, f) : undefined}
                      uploading={replacingType === doc.document_type}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PersonalInfoForm;
